import { all, call, put, takeLatest } from "redux-saga/effects";
import apiService from "services/apiservice";
import { getErrorMessageString } from "utils/utils";
import { loadState } from "library/helpers/sessionStorage";
import {
  GET_CARRIER_CONFIGURATION_LIST_REQUEST,
  ADD_UPDATE_CARRIER_CONFIGURATION_REQUEST,
  GET_CARRIER_MILESTONE_LIST_REQUEST,
  GET_CARRIER_ACTIVITY_LIST_REQUEST,
  GET_CARRIER_CONFIGURATION_DETAILS_REQUEST,
} from "./actionTypes";
import {
  getCarrierConfigurationListFailure,
  getCarrierConfigurationListSuccess,
  addUpdateCarrierConfigurationFailure,
  addUpdateCarrierConfigurationSuccess,
  getCarrierMilestoneListSuccess,
  getCarrierMilestoneListFailure,
  getMilestoneLookupSuccess,
  getMilestoneLookupFailure,
  addUpdateCarrierMilestoneFailure,
  addUpdateCarrierMilestoneSuccess,
  getCarrierActivityListFailure,
  getCarrierActivityListSuccess,
  getCarrierConfigurationDetailsSuccess,
  getCarrierConfigurationDetailsFailure,
} from "./actions";
import {
  CarrierConfigurationList,
  CarrierMilestone,
  MilestoneLookup,
  CarrierActivityList,
} from "./types";

const currentUser = loadState("loggedInUser");
const token = currentUser?.accessToken;

const getRequest = async (url: string) => {
  const { data } = await apiService.get(url, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const postRequest = async (url: string, payload: any) => {
  const { data } = await apiService.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

function* carrierConfigurationListSaga(action: any) {
  try {
    const response: CarrierConfigurationList[] = yield call(
      postRequest,
      action.url,
      action.payload,
    );
    yield put(
      getCarrierConfigurationListSuccess({
        carrierConfiguration: response,
      }),
    );
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      getCarrierConfigurationListFailure({
        error: errorMessages,
      }),
    );
  }
}

function* carrierConfigurationDetailsSaga(action: any) {
  try {
    const response: CarrierConfigurationList[] = yield call(
      getRequest,
      action.url,
    );
    yield put(
      getCarrierConfigurationDetailsSuccess({
        carrierConfiguration: response,
      }),
    );
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      getCarrierConfigurationDetailsFailure({
        error: errorMessages,
      }),
    );
  }
}

function* addUpdateCarrierConfigurationSaga(action: any) {
  try {
    const response: {
      status: string;
      message: string;
    } = yield call(postRequest, action.url, action.payload);
    if (response?.status || response?.message) {
      yield put(
        addUpdateCarrierConfigurationSuccess({
          successMessage: response?.message,
        }),
      );
      action.callback("Success", response?.message);
    }
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      addUpdateCarrierConfigurationFailure({
        error: errorMessages,
      }),
    );
    action.callback("Error", errorMessages);
  }
}

function* carrierMilestoneListSaga(action: any) {
  try {
    const response: CarrierMilestone[] = yield call(
      postRequest,
      action.url,
      action.payload,
    );
    yield put(
      getCarrierMilestoneListSuccess({
        carrierMilestone: response,
      }),
    );
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      getCarrierMilestoneListFailure({
        error: errorMessages,
      }),
    );
  }
}

function* MilestoneLookupSaga(action: any) {
  try {
    const response: MilestoneLookup[] = yield call(getRequest, action.url);
    yield put(
      getMilestoneLookupSuccess({
        milestoneLookup: response,
      }),
    );
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      getMilestoneLookupFailure({
        error: errorMessages,
      }),
    );
  }
}

function* addUpdateCarrierMilestoneSaga(action: any) {
  try {
    const response: {
      status: string;
      message: string;
    } = yield call(postRequest, action.url, action.payload);
    if (response?.status || response?.message) {
      yield put(
        addUpdateCarrierMilestoneSuccess({
          successMessage: response?.message,
        }),
      );
      action.callback("Success", response?.message);
    }
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      addUpdateCarrierMilestoneFailure({
        error: errorMessages,
      }),
    );
    action.callback("Error", errorMessages);
  }
}

function* carrierActivityListSaga(action: any) {
  try {
    const response: CarrierActivityList[] = yield call(
      postRequest,
      action.url,
      action.payload,
    );
    yield put(
      getCarrierActivityListSuccess({
        carrierActivity: response,
      }),
    );
  } catch (e: any) {
    const errorMessages = getErrorMessageString(e);
    yield put(
      getCarrierActivityListFailure({
        error: errorMessages,
      }),
    );
  }
}

function* carrierTrackingSaga() {
  yield all([
    takeLatest(
      GET_CARRIER_CONFIGURATION_LIST_REQUEST,
      carrierConfigurationListSaga,
    ),
    takeLatest(
      GET_CARRIER_CONFIGURATION_DETAILS_REQUEST,
      carrierConfigurationDetailsSaga,
    ),
    takeLatest(
      ADD_UPDATE_CARRIER_CONFIGURATION_REQUEST,
      addUpdateCarrierConfigurationSaga,
    ),
    takeLatest(GET_CARRIER_MILESTONE_LIST_REQUEST, carrierMilestoneListSaga),
    takeLatest("GET_MILESTONE_LOOKUP_REQUEST", MilestoneLookupSaga),
    takeLatest(
      "ADD_UPDATE_CARRIER_MILESTONE_REQUEST",
      addUpdateCarrierMilestoneSaga,
    ),
    takeLatest(GET_CARRIER_ACTIVITY_LIST_REQUEST, carrierActivityListSaga),
  ]);
}

export default carrierTrackingSaga;
